import {
  Box,
  FormControl,
  IconButton,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { FC, FormEvent, KeyboardEvent, useCallback, useState } from 'react';
import { getOnHoverScrollbarStyle } from '../../styles';
import { WbTextField } from '../WbTextField';
import { useChatContext } from './ChatContext';
import { AttachFileIcon, MicIcon, SendIcon } from './icons/icons';
import { WittyMessage } from './Messages';

type TextBoxProps = {
  isMicrophoneActive?: boolean;
  isAttachContentActive?: boolean;
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: theme.palette.background.default,
    padding: theme.spacing(0, 0.5),
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    maxHeight: '200px',
    overflowY: 'auto',
    ...getOnHoverScrollbarStyle(theme, false),
  },
  chatInput: {
    padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px !important`,
    overflowWrap: 'break-word' as const,
    wordBreak: 'break-word' as const,
    ...getOnHoverScrollbarStyle(theme, false),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    cursor: 'pointer',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}));

export const TextBox: FC<TextBoxProps> = ({
  isMicrophoneActive = true,
  isAttachContentActive = true,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const { onNewMessage } = useChatContext();

  const [input, setInput] = useState<string>('');

  const submitMessage = useCallback(() => {
    if (!input) return;
    const message: WittyMessage = { sender: 'you', contentMessage: input };
    setInput('');
    onNewMessage(message);
  }, [input, onNewMessage]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitMessage();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <FormControl style={{ width: '100%' }}>
        <WbTextField
          multiline
          rowsMax={6}
          placeholder="Type message"
          ariaLabel="Type message"
          value={input}
          onKeyDown={onKeyDown}
          onChange={event => {
            setInput(event.target.value);
          }}
          InputProps={{
            classes: {
              root: classes.container,
              input: classes.chatInput,
            },
            startAdornment:
              isMicrophoneActive || isAttachContentActive ? (
                <Box className={classes.actions}>
                  {isMicrophoneActive && (
                    <IconButton size="small" aria-label="record">
                      <MicIcon
                        style={{ color: theme.palette.secondary.main }}
                      />
                    </IconButton>
                  )}
                  {isAttachContentActive && (
                    <IconButton size="small">
                      <AttachFileIcon
                        fontSize="small"
                        aria-label="attach file"
                        style={{ color: theme.palette.secondary.main }}
                      />
                    </IconButton>
                  )}
                </Box>
              ) : undefined,
            endAdornment: (
              <IconButton
                size="small"
                type="submit"
                aria-label="Send message to witty"
              >
                <SendIcon style={{ color: theme.palette.primary.main }} />
              </IconButton>
            ),
          }}
        />
      </FormControl>
    </form>
  );
};
